import { EcspComplianceProfile, useLegalAccountDataState } from '../../../../../utilities/account/useAccountDataLegal'
import { postBeneficialOwner } from '../../../../../utilities/api'
import { notify } from '../../../../../utilities/useNotifications'
import { i18n } from '../../../../../localization'
import { onMounted, onUnmounted, ref } from 'vue'
import {
    ComplianceProfiles,
    InvestmentTargets,
    LegalFatcaDeclaration,
    RiskTolerance,
    TaxResidency,
} from '../../../../../utilities/account/useAccountDataPersonal'
import { useForm } from 'vee-validate'
import { getPlatformSettings } from '../../../../../utilities/usePlatformSettings'
import { useLegalAccountFormState } from '../../../../../utilities/account/useAccountFormLegal'

const saving = ref(false)

async function addBeneficialOwner() {
    saving.value = true
    const { valid } = await form.validate()
    const state = useLegalAccountDataState()

    if (valid) {
        postBeneficialOwner(state.legalProfile.legal_profile_uid, formValues).then(() => {
            Object.assign(formValues, initialValue)
            form.resetForm()
            notify('Success', i18n.global.t('legal_account_managers.manager_invited_notice'))
            state.fetchAccountData().then(() => {
                saving.value = false
            })
        })
    }
}

export function isRequiredLegalComplianceProfileAttribute(category: string, key: string) {
    return getPlatformSettings().legal_compliance_profile_required_fields[category]?.includes(key)
}

export class LegalAcknowledgmentsOne {
    risks_acknowledgement = false
    anti_money_laundering = false
    authorised_signatory = false
}

export class LegalAcknowledgmentsTwo {
    tax_authorities = false
    intergovernmental_tax_exchange = false
    notify_about_changes = false
    information_correct_and_complete = false
}

export class LegalPersonAddress {
    address = null
    postal_code = null
    city = null
    country = null
    postal_address = null
    postal_address_postal_code = null
    postal_address_city = null
    postal_address_country = null
}

export function initializeLegalWidgetForm(validationSchema: any, complianceProfile: keyof ComplianceProfiles = 'esac') {
    const { legalProfile } = useLegalAccountDataState()

    let initialComplianceProfile = null
    if (complianceProfile === 'esac') {
        initialComplianceProfile = legalProfile.compliance_profiles.esac
    } else {
        initialComplianceProfile = legalProfile.compliance_profiles.ecsp
    }

    const form = useForm({
        validationSchema: validationSchema,
        initialValues: initialComplianceProfile,
    })

    const formState = useLegalAccountFormState()

    onMounted(() => {
        // @ts-ignore
        formState.currentForm = form
    })
    onUnmounted(() => {
        formState.currentForm = null
    })
}

export function clearFormValuesNoAnswer(formValues) {
    if (formValues.value.no_answer === true) {
        for (const key in formValues.value) {
            if (key !== 'no_answer') {
                formValues.value[key] = null
            }
        }
    }
}

export function getLegalWidgetFormValidationRules(
    key: keyof LegalEsacComplianceProfile | keyof EcspComplianceProfile,
    complianceProfile: keyof ComplianceProfiles = 'esac',
    denyKey: string = null
) {
    let category = null
    if (complianceProfile === 'esac') {
        category = new LegalEsacComplianceProfile()[key]
    } else {
        category = new EcspComplianceProfile()[key]
    }

    const requiredFields = getPlatformSettings().legal_compliance_profile_required_fields[key]
    const validationRules = {}
    Object.keys(category).forEach((categoryKey) => {
        if (requiredFields && requiredFields.includes(categoryKey)) {
            if (denyKey && denyKey.startsWith(key + '.' + categoryKey)) {
                validationRules[key + '.' + categoryKey] = 'required'
            } else if (
                category[categoryKey] === null ||
                category[categoryKey] === '' ||
                category[categoryKey] === false
            ) {
                validationRules[key + '.' + categoryKey] = denyKey ? 'required_unless:' + denyKey : 'required'
            } else {
                validationRules[key + '.' + categoryKey] = 'multiselect-required'
            }
        }
    })

    return validationRules
}

export async function isLegalComplianceProfileCategoryComplete(
    key: keyof LegalEsacComplianceProfile | keyof EcspComplianceProfile,
    denyKey: string = null
) {
    const currentAccountValidationSchema = getLegalWidgetFormValidationRules(
        key,
        getPlatformSettings().compliance_profile,
        denyKey
    )

    const { legalProfile: account } = useLegalAccountDataState()

    const formData = {
        [key]:
            account.compliance_profiles && account.compliance_profiles[getPlatformSettings().compliance_profile as any]
                ? account.compliance_profiles[getPlatformSettings().compliance_profile as any][key]
                : null,
    }

    const form = useForm({
        validationSchema: currentAccountValidationSchema,
        initialValues: formData,
        validateOnMount: true,
    })

    const validation = await form.validate()
    return validation.valid
}

export class LegalEsacComplianceProfile {
    financial_conditions = new LegalFinancialConditions()
    risk_tolerance = new RiskTolerance()
    fatca_declaration = new LegalFatcaDeclaration()
    tax_situation = new LegalTaxSituationCategory()
    acknowledgements = new LegalAcknowledgements()
    investment_targets = new InvestmentTargets()
    crs_declaration = new CrsClassification()
}

export class CrsClassification {
    classification = null
    stock_exchange_name = null
    affiliate_stock_exchange_name = null
}

export class LegalTaxSituationCategory {
    tax_situation = new LegalTaxSituation()
}

export class LegalAcknowledgements {
    risks_acknowledgement = false
    anti_money_laundering = false
    authorised_signatory = false
    tax_authorities = false
    intergovernmental_tax_exchange = false
    notify_about_changes = false
    information_correct_and_complete = false
}

export class LegalTaxSituation {
    capital_gains = false
    corporate_tax_exemption = false
    group_taxation = false
    foreign_assets = false
    other = ''
}

export class TaxSituationAndResidencies {
    tax_situation = new LegalTaxSituation()
    tax_residencies: TaxResidency[] = [new TaxResidency()]
}

export class LegalPerson {
    id = null
    first_name = null
    last_name = null
    email = null
    nationality = null
    country_of_birth = null
    city_of_birth = null
    date_of_birth = null
    gender = null
    is_manager = false
    is_beneficial_owner = false
    pep = false
    pep_past = false
    pep_past_position = null
    pep_position = null
}

export class CompanyOwnership {
    share_of_ownership = false
    share_of_ownership_company = ''
    control_by_other_means = false
    control_by_other_means_company = ''
    official_senior_manager = false
    official_senior_manager_company = ''
    company_share = null
}

export class LegalFinancialConditions {
    financial_assets = null
    annual_operating_result = null
    dept_capital = null
    industry = null
    properties = false
    source_of_funds = new LegalSourceOfFunds()
}

class LegalSourceOfFunds {
    dividends_or_profits = false
    insurance_claims = false
    sale_of_property = false
    loan = false
    retained_earnings = false
    other = ''
}
